/**
 * Heat Loss Calculator
 * ===
 * Room Model
 * 
 * Extends Backbone Model.
 *
 * Author: Duncan McMillan
 *
 * Copyright (c) August 2024 · Art & Soul Communications Ltd.
 * https://artandsoul.co.uk
 *
 */

'use strict';

import Backbone from 'backbone';

import { HeatLossCalculator } from './../HeatLossCalculator.Object.js';
import { HLC_Configs } from './../HeatLossCalculator.Configs.js';
import { SurfaceModel } from './SurfaceModel.js';

export var RoomModel = Backbone.Model.extend({
    
    defaults: {
        mwt: HLC_Configs.default_mwt,
        default_mwt: HLC_Configs.default_mwt,
        
        width: '',
        length: '',
        height: HLC_Configs.default_room_height,
        temperature: HLC_Configs.default_room_temperature,
        air_changes: HLC_Configs.default_air_changes,
        
        external_wall_count: undefined,
        total_glazed_area: '',
        
        heating_system_type: 'hydronic'
        
        // external_wall_type,
        // floor_type,
        // ceiling_type
        
        // glazing_type
    },
    
    internal_wall_surface: undefined,
    external_wall_surface: undefined,
    floor: undefined,
    ceiling: undefined,
    
    initialize: function(attributes, options) {
        
        // Assume internal walls have an internal door
        this.internal_wall_surface = new SurfaceModel({
            label: 'internal',
            adjacent_temperature: HLC_Configs.default_room_temperature,
            uvalue: HLC_Configs.internal_wall_types[0].uvalue,
            fixture_uvalue: HLC_Configs.internal_doors[0].uvalue,
            fixture_area: HLC_Configs.standard_door_dims
        }, { room: this });
        
        // Default external wall type required in case user selects
        // “No external walls” but then changes this later in the process
        this.external_wall_surface = new SurfaceModel({
            label: 'external',
            adjacent_temperature: HLC_Configs.external_temperature,
            uvalue: HLC_Configs.external_wall_types[0].uvalue
        }, { room: this });
        
        this.floor = new SurfaceModel({
            label: 'floor',
            adjacent_temperature: HLC_Configs.external_temperature
        }, { room: this });
        
        this.ceiling = new SurfaceModel({
            label: 'ceiling',
            adjacent_temperature: HLC_Configs.external_temperature
        }, { room: this });
        
        this.on('change:width', this.setSurfaceDimensions);
        this.on('change:length', this.setSurfaceDimensions);
        this.on('change:height', this.setSurfaceDimensions);
        this.on('change:external_wall_count', this.setSurfaceDimensions);
        
        this.on('change:external_wall_type', this.setExternalWallType);
        this.on('change:floor_type', this.setFloorType);
        this.on('change:ceiling_type', this.setCeilingType);
        
        this.on('change:glazing_type', this.setGlazingType);
        this.on('change:total_glazed_area', this.setGlazedArea);
        
        this.on('change', function() {
            HeatLossCalculator.vent.trigger('roomChange');
        });
        
        return this;
    },
    
    setExternalWallType: function() {
        this.external_wall_surface.set(this.get('external_wall_type'));
        return this;
    },
    
    setFloorType: function() {
        
        var attributes = this.get('floor_type');
        
        if (attributes.adjacent_temperature === undefined) {
            attributes.adjacent_temperature = HLC_Configs.external_temperature;
        }
        
        this.floor.set(attributes);
        
        return this;
    },
    
    setCeilingType: function() {
        
        var attributes = this.get('ceiling_type');
        
        if (attributes.adjacent_temperature === undefined) {
            attributes.adjacent_temperature = HLC_Configs.external_temperature;
        }
        
        this.ceiling.set(attributes);
        
        return this;
    },
    
    setGlazingType: function() {
        this.external_wall_surface.set('fixture_uvalue', this.get('glazing_type')['uvalue']);
        return this;
    },
    
    setGlazedArea: function() {
        this.external_wall_surface.set('fixture_area', this.get('total_glazed_area'));
        return this;
    },
    
    setSurfaceDimensions: function() {
        
        var ext_wall_attr, int_wall_attr, floor_attr, ceiling_attr;
        
        ext_wall_attr = { y: this.get('height') };
        int_wall_attr = { y: this.get('height') };
        
        floor_attr = { x: this.get('length'), y: this.get('width') };
        ceiling_attr = { x: this.get('length'), y: this.get('width') };
        
        switch (Number(this.get('external_wall_count'))) {
            
            case 0 :
                ext_wall_attr.x = 0;
                int_wall_attr.x = 2 * (this.get('length') + this.get('width'));
                break;
            
            case 1 :
                // Assume external wall is the longer
                ext_wall_attr.x = this.get('length');
                int_wall_attr.x = (2 * this.get('width')) + this.get('length');
                break;
            
            case 2 :
                // Assume the two external walls are at right angles
                ext_wall_attr.x = this.get('length') + this.get('width');
                int_wall_attr.x = this.get('length') + this.get('width');
                break;
            
            case 3 :
                // Assume the external walls are two short and one long
                ext_wall_attr.x = (2 * this.get('width')) + this.get('length');
                int_wall_attr.x = this.get('length');
                break;
            
            case 4 :
                ext_wall_attr.x = 2 * (this.get('length') + this.get('width'));
                int_wall_attr.x = 0;
                break;
            
            default :
                // There ain’t no other options
        }
        
        this.internal_wall_surface.set(int_wall_attr);
        this.external_wall_surface.set(ext_wall_attr);
        this.floor.set(floor_attr);
        this.ceiling.set(ceiling_attr);
        
        return this;
    },
    
    /**
     * Area
     *
     */
    area: function() {
        return this.get('length') * this.get('width');
    },
    
    /**
     * Volume
     *
     */
    volume: function() {
        return this.area() * this.get('height');
    },
    
    /**
     * Delta T
     *
     */
    deltaT: function() {
        return this.get('mwt') - this.get('temperature');
    },
    
    /**
     * Ventilation Heat Loss
     * 
     * Return the ventilation heatloss for the
     * room caluculate 
     */
    ventilationHeatLoss: function(){
        
        var temperature_difference, ventilation_heat_loss;
        
        temperature_difference = this.get('temperature') - HLC_Configs.external_temperature;
        
        return this.volume() * this.get('air_changes') * temperature_difference * HLC_Configs.ventilation_factor;
    },
    
    /**
     * Fabric Heat Loss
     *
     */
    fabricHeatLoss: function(){
        
        var fabric_heat_loss = 0;
        
        fabric_heat_loss += this.internal_wall_surface.fabricHeatLoss();
        fabric_heat_loss += this.external_wall_surface.fabricHeatLoss();
        fabric_heat_loss += this.floor.fabricHeatLoss();
        fabric_heat_loss += this.ceiling.fabricHeatLoss();
        
        return fabric_heat_loss; 
    },
    
    /**
     * Total Heat Loss
     *
     * Calculate and return the room’s total actual heat loss.
     */
    totalHeatLoss: function(){
        return Math.round(this.ventilationHeatLoss() + this.fabricHeatLoss());
    },
    
    /**
     * Corrected Heat Loss
     *
     */
    correctedHeatLoss: function(){
        
        var total_heat_loss, corrected_total_heat_loss, correction_key, correction_factor;
        
        correction_key = Math.round(this.deltaT() / 5);
        correction_factor = HLC_Configs.output_correction_factors[correction_key];
        
        total_heat_loss = this.ventilationHeatLoss() + this.fabricHeatLoss();
        corrected_total_heat_loss = total_heat_loss * (1 / correction_factor);
        
        return Math.round(corrected_total_heat_loss);
    }
    
});
